/**
 * OrgRiskDashboard — leadership view across every project.
 *
 * Accessible to Admin / HR / Process Compliance Manager / Process Compliance
 * Officer. Renders:
 *   - Filters (project, PM, level, date range)
 *   - KPI cards (totals + counts)
 *   - Recharts PieChart for distribution
 *   - Recharts BarChart for area-wise average exposure
 *   - Area wise Weight PieChart, Area wise % Exposure BarChart,
 *     Top 15 Risks ScatterChart
 *   - 10x10 heatmap
 *
 * All data comes from `orgRiskDashboardUrl` driven by the filter bar.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import PageTitle from "../../../components/Page Component/PageTitle";
import { fetchData } from "../../../store/actions";
import { RootState } from "../../../store/Store";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import {
  orgRiskDashboardFiltersUrl,
  orgRiskDashboardUrl,
} from "../../../Service/Apis";
import {
  OrgRiskAggregate,
  OrgRiskDashboardFilters,
  RiskLevel,
} from "./Types/RiskManagementTypes";
import { levelColor } from "./components/RiskScoreBadge";
import RiskHeatmap from "./components/RiskHeatmap";
import { isRmwOrgViewer } from "./components/rmwRoles";
import { normalizeOrgDashboard } from "./components/normalizers";

const AREA_COLORS = [
  "#1976d2", "#388e3c", "#ed6c02", "#9c27b0", "#00838f",
  "#c62828", "#4527a0", "#ef6c00", "#2e7d32",
];

const LEVEL_FILTER_OPTIONS: Array<{ value: RiskLevel | "ALL"; label: string }> = [
  { value: "ALL", label: "All levels" },
  { value: "HIGH", label: "High only" },
  { value: "MEDIUM", label: "Medium only" },
  { value: "LOW", label: "Low only" },
];

const OrgRiskDashboard: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );

  useEffect(() => {
    if (!isRmwOrgViewer(selectedRole)) {
      navigate("/landing", { replace: true });
    }
  }, [selectedRole, navigate]);

  const [data, setData] = useState<OrgRiskAggregate | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<OrgRiskDashboardFilters>({
    level: "ALL",
  });
  // Populated from `/dashboard/filters/` — only PMs / projects that actually
  // have an active worksheet show up.
  const [filterOptions, setFilterOptions] = useState<{
    pms: { id: string; name: string; email?: string }[];
    projects: { id: string; name: string }[];
  }>({ pms: [], projects: [] });

  useEffect(() => {
    if (!isRmwOrgViewer(selectedRole)) return;
    dispatch(fetchData(orgRiskDashboardFiltersUrl))
      .then((res: any) => {
        if (!res) return;
        setFilterOptions({
          pms: Array.isArray(res.pms) ? res.pms : [],
          projects: Array.isArray(res.projects) ? res.projects : [],
        });
      })
      .catch(() => {
        // Filter dropdowns will simply remain empty — non-fatal.
      });
  }, [dispatch, selectedRole]);

  const fetchDashboard = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.pm) params.set("pm", filters.pm);
    if (filters.project) params.set("project", filters.project);
    if (filters.level && filters.level !== "ALL") params.set("level", filters.level);
    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to) params.set("date_to", filters.date_to);
    if (selectedRole) params.set("role", selectedRole);
    dispatch(fetchData(`${orgRiskDashboardUrl}?${params.toString()}`))
      .then((res: any) =>
        // Backend exposes a nested shape (distribution[], area_averages[].area,
        // 10x10 heatmap grid); flatten/rename so the page can read it directly.
        setData(normalizeOrgDashboard(res) as OrgRiskAggregate)
      )
      .catch(() => showToast("Failed to load dashboard.", "error", false))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filters, selectedRole]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const distributionData = data
    ? [
        { name: "High", value: data.distribution.high, color: levelColor("HIGH") },
        { name: "Medium", value: data.distribution.medium, color: levelColor("MEDIUM") },
        { name: "Low", value: data.distribution.low, color: levelColor("LOW") },
      ]
    : [];

  const areaBars =
    data?.area_averages.map((a) => ({
      area: a.area_name || "—",
      Exposure: Math.round(Number(a.avg_exposure ?? 0)),
      High: a.high_count,
      Medium: a.medium_count,
      Low: a.low_count,
    })) ?? [];

  const totalRiskCount =
    data?.area_averages.reduce((sum, a) => sum + (a.risk_count ?? 0), 0) ?? 0;

  const areaWeightData =
    data?.area_averages.map((a) => ({
      name: a.area_name || "—",
      value:
        totalRiskCount > 0
          ? Math.round(((a.risk_count ?? 0) / totalRiskCount) * 10000) / 100
          : 0,
      count: a.risk_count ?? 0,
    })) ?? [];

  const exposurePctBars =
    data?.area_averages.map((a) => ({
      area: a.area_name || "—",
      "% Exposure": Math.round(Number(a.avg_exposure ?? 0) * 100) / 100,
    })) ?? [];

  const topRisksScatter = (data?.top_risks ?? [])
    .slice(0, 15)
    .map((r, i) => ({
      rank: i + 1,
      exposure: r.exposure,
      area_name: r.area_name,
      contributor_name: r.contributor_name,
      project_name: r.project_name,
    }));

  if (loading && !data) {
    return (
      <Container maxWidth="xl" sx={{ mt: 12, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 6 }}>
      <PageTitle>Risk Management — Org Dashboard</PageTitle>

      {/* Filters — single horizontal strip. Uses flexbox so every control
          (including Refresh) sits on the same line on md+. Wraps gracefully
          on narrow screens. */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <FormControl size="small" sx={{ flex: "1 1 220px", minWidth: 200 }}>
            {/* `shrink` + `notched` keep the floating label parked at the
                top even when `displayEmpty` shows the "All …" placeholder,
                so the two strings don't overlap. */}
            <InputLabel shrink id="rmw-dashboard-project-label">
              Project
            </InputLabel>
            <Select
              labelId="rmw-dashboard-project-label"
              label="Project"
              notched
              value={filters.project ?? ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  project: (e.target.value as string) || undefined,
                }))
              }
              displayEmpty
              renderValue={(value) => {
                if (!value) return <em>All projects</em>;
                const match = filterOptions.projects.find(
                  (p) => p.id === value
                );
                return match?.name ?? String(value);
              }}
            >
              <MenuItem value="">
                <em>All projects</em>
              </MenuItem>
              {filterOptions.projects.length === 0 ? (
                <MenuItem value="" disabled>
                  <em>(no projects have a risk worksheet yet)</em>
                </MenuItem>
              ) : (
                filterOptions.projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: "1 1 200px", minWidth: 180 }}>
            <InputLabel shrink id="rmw-dashboard-pm-label">
              Project Manager
            </InputLabel>
            <Select
              labelId="rmw-dashboard-pm-label"
              label="Project Manager"
              notched
              value={filters.pm ?? ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  pm: (e.target.value as string) || undefined,
                }))
              }
              displayEmpty
              renderValue={(value) => {
                if (!value) return <em>All PMs</em>;
                const match = filterOptions.pms.find((p) => p.id === value);
                return match
                  ? `${match.name}${match.email ? ` — ${match.email}` : ""}`
                  : String(value);
              }}
            >
              <MenuItem value="">
                <em>All PMs</em>
              </MenuItem>
              {filterOptions.pms.length === 0 ? (
                <MenuItem value="" disabled>
                  <em>(no PM assigned to any worksheet yet)</em>
                </MenuItem>
              ) : (
                filterOptions.pms.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                    {p.email ? ` — ${p.email}` : ""}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: "0 1 140px", minWidth: 120 }}>
            <InputLabel>Level</InputLabel>
            <Select
              label="Level"
              value={filters.level ?? "ALL"}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  level: e.target.value as RiskLevel | "ALL",
                }))
              }
            >
              {LEVEL_FILTER_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={filters.date_from ?? ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, date_from: e.target.value }))
            }
            sx={{ flex: "0 1 160px", minWidth: 140 }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={filters.date_to ?? ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, date_to: e.target.value }))
            }
            sx={{ flex: "0 1 160px", minWidth: 140 }}
          />
          {/* `ml: "auto"` pins Refresh to the right edge regardless of how
              wide the other controls become. */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchDashboard}
            disabled={loading}
            sx={{ ml: "auto", flex: "0 0 auto" }}
          >
            Refresh
          </Button>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          Pick a project to scope the dashboard to a single worksheet, or
          leave it on <em>All projects</em> for the full org view.
        </Typography>
      </Paper>

      {!data ? (
        <Alert severity="info">No data yet.</Alert>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              {
                label: "Total Risks",
                value:
                  data.distribution.high +
                  data.distribution.medium +
                  data.distribution.low,
                color: "#1976d2",
              },
              { label: "HIGH", value: data.distribution.high, color: "#c62828" },
              { label: "MEDIUM", value: data.distribution.medium, color: "#ed6c02" },
              { label: "LOW", value: data.distribution.low, color: "#2e7d32" },
              { label: "Open", value: data.distribution.open, color: "#0288d1" },
              {
                label: "Mitigated",
                value: data.distribution.mitigated,
                color: "#388e3c",
              },
            ].map((k) => (
              <Grid key={k.label} item xs={6} sm={4} md={2}>
                <Card elevation={2}>
                  <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                    <Typography variant="h4" fontWeight="bold" color={k.color}>
                      {k.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {k.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: 360 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Risk distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={distributionData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={100}
                      label
                    >
                      {distributionData.map((d, i) => (
                        <Cell key={i} fill={d.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2, height: 360 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Average exposure by area
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={areaBars}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="area" interval={0} angle={-20} textAnchor="end" height={70} />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="High" stackId="a" fill={levelColor("HIGH")} />
                    <Bar dataKey="Medium" stackId="a" fill={levelColor("MEDIUM")} />
                    <Bar dataKey="Low" stackId="a" fill={levelColor("LOW")} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: 400 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Area wise Weight
                </Typography>
                <ResponsiveContainer width="100%" height={340}>
                  <PieChart>
                    <Pie
                      data={areaWeightData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={110}
                      label={({ value }) => `${value}%`}
                    >
                      {areaWeightData.map((_d, i) => (
                        <Cell
                          key={i}
                          fill={AREA_COLORS[i % AREA_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value: number, _name: string, props: any) => [
                        `${value}% (${props.payload.count} risks)`,
                        props.payload.name,
                      ]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: 400 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Area wise % Exposure
                </Typography>
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart data={exposurePctBars}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="area"
                      interval={0}
                      angle={-35}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <RechartsTooltip
                      formatter={(value: number) => [`${value}%`, "Exposure"]}
                    />
                    <Bar dataKey="% Exposure" fill="#1976d2">
                      {exposurePctBars.map((_d, i) => (
                        <Cell
                          key={i}
                          fill={AREA_COLORS[i % AREA_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 2, overflow: "auto" }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Probability × Impact heatmap
                </Typography>
                <RiskHeatmap cells={data.heatmap} cellSize={28} />
                <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    label="HIGH"
                    sx={{ bgcolor: levelColor("HIGH"), color: "#fff" }}
                  />
                  <Chip
                    size="small"
                    label="MEDIUM"
                    sx={{ bgcolor: levelColor("MEDIUM"), color: "#fff" }}
                  />
                  <Chip
                    size="small"
                    label="LOW"
                    sx={{ bgcolor: levelColor("LOW"), color: "#fff" }}
                  />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Top 15 Risks
                  </Typography>
                  <Button
                    size="small"
                    onClick={() =>
                      navigate("/pms/risk-management/top-risks")
                    }
                  >
                    View all
                  </Button>
                </Box>
                <ResponsiveContainer width="100%" height={340}>
                  <ScatterChart
                    margin={{ top: 10, right: 20, bottom: 30, left: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="rank"
                      type="number"
                      domain={[0, 16]}
                      tickCount={16}
                      tick={{ fontSize: 11 }}
                    >
                      <Label
                        value="Risk in Rank Order"
                        position="bottom"
                        offset={10}
                        style={{ fontSize: 12 }}
                      />
                    </XAxis>
                    <YAxis
                      dataKey="exposure"
                      type="number"
                      domain={[0, 100]}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <RechartsTooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        const proj = d.project_name?.length > 25
                          ? `${d.project_name.slice(0, 25)}...`
                          : d.project_name;
                        return (
                          <Paper sx={{ p: 1, fontSize: 12 }} elevation={3}>
                            {proj && (
                              <Typography variant="caption" display="block" fontWeight="bold">
                                {proj}
                              </Typography>
                            )}
                            <Typography variant="body2" display="block">
                              {d.area_name}: {d.contributor_name}
                            </Typography>
                            <Typography variant="caption" display="block" color="error">
                              Exposure: {d.exposure}%
                            </Typography>
                          </Paper>
                        );
                      }}
                    />
                    <Scatter
                      data={topRisksScatter}
                      fill="#c62828"
                      shape="circle"
                    />
                  </ScatterChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default OrgRiskDashboard;
